/**
 * The beta band (decision 0044): a yellow band with words rolling under a button, on the start
 * sheet and the menu's home, never in play. A web page's way of saying the game is not finished,
 * until the launch; SHOW_BETA in features.ts takes it away. The rolling words are one run of the
 * phrases, drawn twice so the track can move by exactly one and start again. Under reduced motion
 * only the first phrase shows, standing still, and that phrase is what a screen reader hears.
 */
import "./beta.css";
import type { StringKey } from "../i18n.ts";
import { esc, icon } from "./dom.ts";

type T = (key: StringKey) => string;

/** The parts of the page About the beta, in the menu and at /beta. */
export const BETA_PARTS: readonly (readonly [title: StringKey, body: StringKey])[] = [
  ["beta.bugsTitle", "beta.bugs"],
  ["beta.languageTitle", "beta.language"],
];

/** Rounds of the phrases in one run, so a run is wider than any band. */
const ROUNDS = 3;
/** Seconds a letter takes to pass, an even pace in a language of any length. */
const SECONDS_A_LETTER = 0.16;
/** A character of the scripts without capitals is about twice as wide as a letter of Archivo's. */
const WIDE_LETTER = 2;
const WIDE = /[⺀-￿]/u;

export function betaBand(t: T, action: string): string {
  const first = t("beta.ticker1");
  const round = `${[first, t("beta.ticker2"), t("beta.ticker3")].join(" • ")} • `;
  const letters = Array.from(round).reduce((n, c) => n + (WIDE.test(c) ? WIDE_LETTER : 1), 0);
  const seconds = (letters * ROUNDS * SECONDS_A_LETTER).toFixed(1);
  const run = `<span>${esc(round.repeat(ROUNDS))}</span>`;
  return `
    <div class="beta">
      <div class="beta-ticker">
        <span class="beta-still">${esc(first)}</span>
        <div class="beta-track" aria-hidden="true" style="--beta-seconds: ${seconds}s">${run}${run}</div>
      </div>
      <button type="button" class="beta-more" data-action="${action}" aria-label="${esc(t("beta.title"))}">${esc(t("beta.more"))}${icon("chevronRight", 16)}</button>
    </div>`;
}
